'use client';

import { useEffect, useState } from 'react';
import { useCart, useCommerceSource, type ProductDetail } from '@akademi/site-builder-runtime';
import { totalOf, type LineTotal, type LiveMoney } from './money';

/**
 * A product line the operator authored on a home block: which product, and how many.
 *
 * <p>Products are named by SLUG, the identity the catalogue publishes at `/p/{slug}`, and are
 * read live. A price typed into page data would be frozen the moment the template was applied
 * and then read as current — the reason the old home page carried no counts or prices at all.
 */
export interface LiveLine {
  slug: string;
  quantity?: number;
}

/** `undefined` while loading, `null` when the catalogue has no such product. */
export type LiveProduct = ProductDetail | null | undefined;

export interface LiveLines {
  /** One entry per authored line, in order. */
  products: LiveProduct[];
  loading: boolean;
  /** Every line resolved to a product that can be bought. */
  buyable: boolean;
  /** The lines' total on both bases, when every line resolved and is priced. */
  total: LineTotal | null;
}

const quantityOf = (line: LiveLine) =>
  Number.isInteger(line.quantity) && (line.quantity as number) > 0 ? (line.quantity as number) : 1;

export function useLiveLines(lines: LiveLine[] | undefined): LiveLines {
  const commerce = useCommerceSource();
  const slugs = (lines ?? []).map((line) => line.slug.trim());
  const key = slugs.join('|');
  const [products, setProducts] = useState<LiveProduct[]>(() => slugs.map(() => undefined));

  useEffect(() => {
    if (!commerce || slugs.length === 0) return;
    let cancelled = false;
    Promise.all(
      slugs.map((slug) =>
        slug ? commerce.getProductBySlug(slug).catch(() => null) : Promise.resolve(null),
      ),
    ).then((found) => {
      if (!cancelled) setProducts(found);
    });
    return () => {
      cancelled = true;
    };
    // `key` is the slugs' identity; the array itself is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commerce, key]);

  const loading = !!commerce && products.some((product) => product === undefined);
  const resolved = products.every((product): product is ProductDetail => !!product);
  const total = resolved
    ? totalOf(
        (lines ?? []).map((line, index) => ({
          price: products[index]!.price as LiveMoney | undefined,
          quantity: quantityOf(line),
        })),
      )
    : null;
  const buyable = resolved && products.length > 0 && products.every((product) => product!.availability !== 'OUT_OF_STOCK');

  return { products, loading, buyable, total };
}

export type AddState = 'idle' | 'busy' | 'added' | 'failed';

/**
 * Adds every line to the basket, one after the other — the basket API takes one product per
 * call, and running them in parallel would race the basket token on a first add.
 *
 * <p>`available` is false when the shop runs no basket (a quote-led site), and the blocks then
 * show their link instead of a button that cannot work.
 */
export function useAddLines(lines: LiveLine[] | undefined, products: LiveProduct[]) {
  const cart = useCart();
  const [state, setState] = useState<AddState>('idle');

  async function add() {
    if (!cart || !cart.available || state === 'busy') return;
    setState('busy');
    let ok = true;
    for (const [index, line] of (lines ?? []).entries()) {
      const product = products[index];
      if (!product) {
        ok = false;
        continue;
      }
      ok = (await cart.add({ productId: product.id, quantity: String(quantityOf(line)) })) && ok;
    }
    setState(ok ? 'added' : 'failed');
  }

  return { available: !!cart?.available, busy: state === 'busy' || !!cart?.busy, state, add };
}
